/**
 * Dev-only mock for the Deal Hub outcome queue.
 *
 * DEVELOPMENT ONLY. Inert unless BOTH hold:
 *   - the bundle is a dev build (`import.meta.env.DEV`), and
 *   - the session was opened with `?mockQueue=1`.
 *
 * The flag is sticky for the tab (sessionStorage) so navigating inside the app
 * keeps the mock alive; `?mockQueue=0` clears it.
 *
 * `?mockQueue=1` queues rules 1–6 — the Sold half, which is what a sitting
 * usually looks like. `?mockQueue=removed` queues the Removed entries (both
 * rule-8 flavours and rule 9). `?mockQueue=all` queues everything in rule order.
 *
 * Why this exists: the queue endpoint has no server implementation yet — the
 * §4 fields are still in flight upstream — so there is otherwise no way to see
 * these screens at all.
 *
 * A `fetch` shim rather than MSW: MSW would need a service worker registered
 * from `public/`, which does not work in every dev browser, and this is three
 * routes. Every other request is handed straight to the real `fetch`, so the
 * rest of the app talks to the local API exactly as it always does.
 */

const FLAG_KEY = 'mockOutcomeQueue'

/** Which fixtures each mode queues, by §1 rule number. */
const MODES: Record<string, number[]> = {
  // The Sold half — what an ordinary sitting looks like.
  '1': [1, 2, 3, 4, 5, 6],
  // Both Removed entry paths: a prefilled reason, and the open question.
  // Both rule-8 flavours (live MLS listing, terminated agreement) plus the open question.
  removed: [8, 10, 9],
  all: [1, 2, 3, 4, 5, 6, 7, 8, 10, 9],
}

export function shouldMockOutcomeQueue(): boolean {
  if (!import.meta.env.DEV) return false

  const requested = new URLSearchParams(window.location.search).get('mockQueue')
  if (requested && requested in MODES) sessionStorage.setItem(FLAG_KEY, requested)
  if (requested === '0') sessionStorage.removeItem(FLAG_KEY)

  return sessionStorage.getItem(FLAG_KEY) !== null
}

/** Path of a Request/URL/string, whatever form fetch was called in. */
function pathOf(input: RequestInfo | URL): string {
  const raw =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  try {
    return new URL(raw, window.location.origin).pathname
  } catch {
    return raw
  }
}

function json(body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  })
}

export async function startOutcomeQueueMock(): Promise<void> {
  // Dynamic import so the fixtures never reach a production bundle.
  const { OUTCOME_QUEUE_FIXTURES } = await import('./outcomeQueueFixtures')

  // Fixtures are stored in rule order, so a rule number is its index + 1.
  const rules = MODES[sessionStorage.getItem(FLAG_KEY) ?? '1'] ?? MODES['1']
  const queued = rules.map((rule) => OUTCOME_QUEUE_FIXTURES[rule - 1])

  // Properties already worked in this sitting, so a reload does not re-serve
  // them — the real endpoint drops a property once its outcome row exists.
  const recorded = new Set<string>()

  const realFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const path = pathOf(input)
    const method = (
      init?.method ??
      (input instanceof Request ? input.method : 'GET')
    ).toUpperCase()

    if (method === 'GET' && path.endsWith('/properties/outcome-queue')) {
      const items = queued.filter((item) => !recorded.has(item.property_id))
      return json({ items, total: items.length })
    }

    const outcome = /\/properties\/([^/]+)\/outcome$/.exec(path)
    if (method === 'POST' && outcome) {
      recorded.add(outcome[1])
      return json({
        id: 'mock-outcome',
        property_id: outcome[1],
        offers_accepted: 1,
        offers_rejected: 2,
      })
    }

    if (method === 'PATCH' && /\/properties\/[^/]+\/outcome\/feedback$/.test(path)) {
      return json({ id: 'mock-outcome' })
    }

    return realFetch(input, init)
  }

  console.info(
    `[dev] Deal Hub outcome queue is mocked (${queued.length} properties). ` +
      '?mockQueue=removed, ?mockQueue=all, ?mockQueue=0 to stop.'
  )
}
